import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, MapPin, Mail, ArrowRight, X, AlertCircle, CheckCircle2 } from "lucide-react";
import api, { fmtErr } from "../api";
import { useI18n } from "../i18n";
import useSeoHead from "../useSeoHead";

/**
 * Public supplier profile — /suppliers/:slug
 *
 * For unclaimed profiles: prominent claim CTA, no RFQ form (replaced with
 * "Are you a US buyer? Submit RFQ inquiry" link).
 * For claimed profiles: shows full microsite info + RFQ button.
 *
 * NOTE: while React fetches & renders this page client-side too, the
 * Express seoMiddleware ALSO injects a <noscript> block + Organization
 * JSON-LD into the raw HTML response so Google indexes the content
 * even without executing JS. See /app/frontend/seoMiddleware.js.
 */
export default function SupplierPublic() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showClaim, setShowClaim] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get(`/suppliers/${slug}`);
        if (!cancelled) setSupplier(data);
      } catch (e) {
        if (!cancelled) setError(fmtErr(e.response?.data?.detail || e.message));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // SEO head — title, description, JSON-LD Organization schema.
  const seoTitle = supplier
    ? `${supplier.company_name} — IMMEX Supplier in Ciudad Juárez | Corridor Digital`
    : "Supplier — Corridor Digital";
  const seoDesc = supplier
    ? `${supplier.company_name} is an IMMEX-certified ${supplier.industry || "manufacturing"} supplier in ${supplier.city || "Ciudad Juárez"}, ${supplier.state || "Chihuahua"}, Mexico. View certifications, capabilities, and submit an RFQ on Corridor Digital.`
    : "IMMEX supplier directory on Corridor Digital.";

  const jsonLd = useMemo(() => {
    if (!supplier) return [];
    return [
      {
        id: "ld-supplier-org",
        payload: {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: supplier.company_name,
          url: `https://corridordigital.net/suppliers/${supplier.slug}`,
          address: {
            "@type": "PostalAddress",
            streetAddress: supplier.street_address || undefined,
            addressLocality: supplier.city || "Ciudad Juárez",
            addressRegion: supplier.state || "Chihuahua",
            postalCode: supplier.zip_code || undefined,
            addressCountry: supplier.country || "MX",
          },
          identifier: supplier.immex_number
            ? [
                {
                  "@type": "PropertyValue",
                  propertyID: "IMMEX",
                  value: supplier.immex_number,
                },
              ]
            : undefined,
          industry: supplier.industry || undefined,
        },
      },
    ];
  }, [supplier]);

  useSeoHead({
    title: seoTitle,
    description: seoDesc,
    path: `/suppliers/${slug}`,
    lang,
    jsonLd,
  });

  if (loading) {
    return (
      <div className="container-cd py-20" data-testid="supplier-loading">
        <div className="font-mono text-sm overline">LOADING…</div>
      </div>
    );
  }
  if (error || !supplier) {
    return (
      <div className="container-cd py-20" data-testid="supplier-not-found">
        <div className="overline" style={{ color: "var(--orange)" }}>
          {lang === "es" ? "PROVEEDOR NO ENCONTRADO" : "SUPPLIER NOT FOUND"}
        </div>
        <h1 className="font-display text-4xl mt-3">
          {lang === "es" ? "No pudimos encontrar este proveedor" : "We couldn't find this supplier"}
        </h1>
        <p className="mt-3 text-sm" style={{ color: "var(--ink-2)" }}>{error || ""}</p>
        <Link to="/directory" className="btn btn-dark btn-sm mt-6 inline-flex">
          {lang === "es" ? "Ver directorio" : "Browse directory"} →
        </Link>
      </div>
    );
  }

  const isUnclaimed = supplier.profile_status === "unclaimed";
  const isFoundingMember = supplier.profile_status === "verified_founding_member";

  return (
    <div data-testid="supplier-page">
      {/* Hero */}
      <section
        className="container-cd pt-16 pb-10"
        style={{ borderBottom: "1px solid var(--line)" }}
      >
        <div className="overline" style={{ color: "var(--ink-2)" }}>
          CORRIDOR · IMMEX SUPPLIER ·{" "}
          <Link to="/directory" className="hover:underline">
            {lang === "es" ? "DIRECTORIO" : "DIRECTORY"}
          </Link>
        </div>

        <div className="mt-4 flex flex-wrap items-start gap-3">
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-4xl"
            data-testid="supplier-company-name"
          >
            {supplier.company_name}
          </h1>
        </div>

        {/* Industry + location row */}
        <div className="mt-5 flex flex-wrap items-center gap-2">
          {supplier.industry ? (
            <span className="cd-chip cd-chip-dark" data-testid="supplier-industry-badge">
              {supplier.industry}
            </span>
          ) : null}
          {supplier.immex_number ? (
            <span
              className="cd-chip cd-chip-orange inline-flex items-center gap-1.5"
              data-testid="supplier-immex-badge"
              title={`IMMEX ${supplier.immex_number}`}
            >
              <ShieldCheck size={12} />
              {lang === "es" ? "IMMEX listado por gobierno" : "Government-listed IMMEX"} · {supplier.immex_number}
            </span>
          ) : null}
          {isFoundingMember ? (
            <span className="cd-chip cd-chip-orange" data-testid="supplier-founding-badge">
              ✦ {lang === "es" ? "Miembro Fundador" : "Founding Member"}
            </span>
          ) : null}
          {isUnclaimed ? (
            <span
              className="cd-chip"
              style={{ background: "var(--bg)", color: "var(--ink-2)", border: "1px solid var(--line)" }}
              data-testid="supplier-unclaimed-badge"
            >
              {lang === "es" ? "Sin reclamar" : "Unclaimed"}
            </span>
          ) : null}
        </div>

        <div
          className="mt-3 flex items-center gap-2 font-mono text-xs"
          style={{ color: "var(--ink-2)" }}
        >
          <MapPin size={12} />
          {supplier.city || "Ciudad Juárez"}, {supplier.state || "Chihuahua"}, {supplier.country || "Mexico"}
        </div>

        {/* CLAIM CTA — only when unclaimed */}
        {isUnclaimed ? (
          <>
            <BuyerActivityBlock
              views={supplier.profile_views || 0}
              onClaimClick={() => setShowClaim(true)}
            />
            <div className="mt-8" data-testid="supplier-claim-cta-wrap">
            <button
              type="button"
              onClick={() => setShowClaim(true)}
              className="btn btn-primary inline-flex items-center gap-2"
              style={{ fontSize: 16, padding: "14px 22px" }}
              data-testid="supplier-claim-btn"
            >
              {lang === "es"
                ? "¿Es esta tu empresa? Reclama tu perfil gratis"
                : "Is this your company? Claim your profile for free"}
              <ArrowRight size={16} />
            </button>
            <p
              className="mt-3 text-sm max-w-2xl"
              style={{ color: "var(--ink-2)" }}
              data-testid="supplier-claim-helper"
            >
              {lang === "es"
                ? "Reclamar es gratis. Tarda menos de 2 minutos y desbloquea RFQs directos de compradores estadounidenses."
                : "Claiming is free. Takes less than 2 minutes and unlocks direct RFQs from US procurement buyers."}
            </p>
          </div>
          </>
        ) : null}
      </section>

      {/* Body grid */}
      <div className="container-cd py-12 grid md:grid-cols-12 gap-10">
        {/* Left column */}
        <main className="md:col-span-8 space-y-10">
          {/* Description block */}
          <section>
            <div className="overline">{lang === "es" ? "DESCRIPCIÓN" : "OVERVIEW"}</div>
            <div className="mt-3 text-base leading-relaxed" data-testid="supplier-description">
              {(lang === "es" ? supplier.description_es : supplier.description_en) ||
                supplier.description_en ||
                supplier.description_es || (
                  <p style={{ color: "var(--ink-2)" }}>
                    {isUnclaimed
                      ? lang === "es"
                        ? `${supplier.company_name} aparece en el registro IMMEX oficial de la Secretaría de Economía de México (abril 2026). Cuando esta empresa reclame su perfil, agregarán una descripción bilingüe, productos/servicios, certificaciones y capacidad.`
                        : `${supplier.company_name} is listed in the official Mexican IMMEX registry (Secretaría de Economía, April 2026). When this company claims their profile, they will add a bilingual description, products/services, certifications, and capacity.`
                      : lang === "es"
                      ? "Esta empresa aún no ha agregado una descripción detallada."
                      : "This company has not yet added a detailed description."}
                  </p>
                )}
            </div>
          </section>

          {/* What a claimed profile includes (only show on unclaimed) */}
          {isUnclaimed ? (
            <section
              className="cd-card p-6 md:p-7"
              data-testid="supplier-claim-benefits"
              style={{ background: "rgba(255,107,0,0.04)", borderColor: "var(--orange)" }}
            >
              <div className="overline" style={{ color: "var(--orange)" }}>
                {lang === "es"
                  ? "COMPLETA TU PERFIL PARA SER DESCUBIERTO POR COMPRADORES DE EE.UU."
                  : "COMPLETE YOUR PROFILE TO GET DISCOVERED BY US BUYERS"}
              </div>
              <h3 className="font-heading text-2xl mt-3">
                {lang === "es"
                  ? "Un perfil reclamado incluye:"
                  : "A claimed profile includes:"}
              </h3>
              <ul className="mt-4 space-y-2 text-sm">
                {[
                  lang === "es"
                    ? "Descripción bilingüe (ES/EN) de su empresa"
                    : "Bilingual description (ES/EN) of your company",
                  lang === "es"
                    ? "Lista detallada de productos y servicios"
                    : "Detailed list of products and services",
                  lang === "es"
                    ? "Certificaciones (ISO, IATF, AS9100, FDA, etc.)"
                    : "Certifications (ISO, IATF, AS9100, FDA, etc.)",
                  lang === "es"
                    ? "Capacidad de planta y empleados"
                    : "Plant capacity and headcount",
                  lang === "es"
                    ? "Formulario de contacto RFQ directo"
                    : "Direct RFQ contact form",
                  lang === "es" ? "Galería de fotos de la planta" : "Plant photo gallery",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 size={16} style={{ color: "var(--orange)", marginTop: 2 }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => setShowClaim(true)}
                className="btn btn-primary mt-6 inline-flex items-center gap-2"
                data-testid="supplier-claim-benefits-btn"
              >
                {lang === "es" ? "Reclama tu perfil gratis" : "Claim your profile free"}
                <ArrowRight size={14} />
              </button>
            </section>
          ) : null}

          {/* Buyer RFQ link (when unclaimed — show fallback) */}
          {isUnclaimed ? (
            <section className="cd-card p-6" data-testid="supplier-buyer-fallback">
              <div className="overline">{lang === "es" ? "¿ERES COMPRADOR?" : "ARE YOU A BUYER?"}</div>
              <p className="mt-3 text-sm" style={{ color: "var(--ink-2)" }}>
                {lang === "es"
                  ? "Este proveedor aún no ha reclamado su perfil. Si eres un comprador de EE.UU. y estás interesado, podemos contactarlo en tu nombre."
                  : "This supplier has not yet claimed their profile. Are you a US buyer? We can reach out on your behalf."}
              </p>
              <Link
                to={`/founding?ref=supplier-rfq-${slug}`}
                className="btn btn-dark btn-sm mt-4 inline-flex items-center gap-2"
                data-testid="supplier-buyer-rfq-link"
              >
                {lang === "es" ? "Enviar consulta RFQ" : "Submit an RFQ inquiry"} →
              </Link>
            </section>
          ) : (
            // Claimed → show RFQ contact button
            <section className="cd-card p-6" data-testid="supplier-rfq-block">
              <div className="overline">{lang === "es" ? "ENVIAR RFQ" : "SUBMIT RFQ"}</div>
              <p className="mt-3 text-sm" style={{ color: "var(--ink-2)" }}>
                {lang === "es"
                  ? "Contacta a este proveedor directamente con tu solicitud de cotización."
                  : "Contact this supplier directly with your request for quotation."}
              </p>
              <Link
                to={`/m/${supplier.slug}`}
                className="btn btn-primary btn-sm mt-4 inline-flex items-center gap-2"
                data-testid="supplier-rfq-btn"
              >
                {lang === "es" ? "Ver microsite y enviar RFQ" : "View microsite & send RFQ"} →
              </Link>
            </section>
          )}
        </main>

        {/* Right column — facts */}
        <aside className="md:col-span-4 space-y-5" data-testid="supplier-facts">
          <div className="cd-card p-5">
            <div className="overline">{lang === "es" ? "DATOS" : "FACTS"}</div>
            <dl className="mt-4 space-y-3 text-sm">
              <Row label={lang === "es" ? "INDUSTRIA" : "INDUSTRY"} value={supplier.industry || "—"} />
              <Row label={lang === "es" ? "CIUDAD" : "CITY"} value={supplier.city || "—"} />
              <Row label={lang === "es" ? "ESTADO" : "STATE"} value={supplier.state || "—"} />
              <Row label={lang === "es" ? "PAÍS" : "COUNTRY"} value={supplier.country || "—"} />
              <Row
                label="IMMEX"
                value={supplier.immex_number || "—"}
                testid="supplier-immex-fact"
              />
              {supplier.immex_status ? (
                <Row label={lang === "es" ? "ESTADO IMMEX" : "IMMEX STATUS"} value={supplier.immex_status} />
              ) : null}
              {supplier.program_type ? (
                <Row label={lang === "es" ? "PROGRAMA" : "PROGRAM"} value={supplier.program_type} />
              ) : null}
              {supplier.data_source ? (
                <Row label={lang === "es" ? "FUENTE" : "SOURCE"} value={supplier.data_source} />
              ) : null}
            </dl>
          </div>
        </aside>
      </div>

      {showClaim ? (
        <ClaimModal
          supplier={supplier}
          lang={lang}
          onClose={() => setShowClaim(false)}
          onSuccess={() =>
            setSupplier((s) => (s ? { ...s, profile_status: "claim_pending" } : s))
          }
        />
      ) : null}
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt
        className="overline flex-shrink-0"
        style={{ color: "var(--ink-2)" }}
      >
        {label}
      </dt>
      <dd className="text-right break-words" data-testid={testid}>
        {value}
      </dd>
    </div>
  );
}

function ClaimModal({ supplier, lang, onClose, onSuccess }) {
  const [form, setForm] = useState({
    email: "",
    name: "",
    title: "",
    immex_number: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }
  async function submit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post(`/suppliers/${supplier.slug}/claim`, form);
      setDone(true);
      onSuccess?.();
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={onClose}
      data-testid="supplier-claim-modal-overlay"
    >
      <div
        className="cd-card"
        style={{
          background: "white",
          maxWidth: 520,
          width: "100%",
          padding: 28,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="supplier-claim-modal"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="overline" style={{ color: "var(--orange)" }}>
              {lang === "es" ? "RECLAMAR PERFIL" : "CLAIM PROFILE"}
            </div>
            <h2 className="font-display text-3xl mt-2 leading-tight">
              {supplier.company_name}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="btn btn-ghost"
            style={{ padding: 6, minHeight: 0 }}
            data-testid="supplier-claim-close"
          >
            <X size={18} />
          </button>
        </div>

        {done ? (
          <div className="mt-6" data-testid="supplier-claim-success">
            <div className="flex items-start gap-3">
              <CheckCircle2 size={22} style={{ color: "var(--green)", marginTop: 2 }} />
              <div>
                <div className="font-heading text-lg">
                  {lang === "es" ? "¡Recibido!" : "Got it!"}
                </div>
                <p className="text-sm mt-1" style={{ color: "var(--ink-2)" }}>
                  {lang === "es"
                    ? "Revisaremos tu reclamación y te enviaremos los siguientes pasos por correo en menos de 24 horas hábiles."
                    : "We'll review your claim and email you next steps within 24 business hours."}
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="btn btn-dark btn-sm mt-5"
                  data-testid="supplier-claim-done-btn"
                >
                  {lang === "es" ? "Cerrar" : "Close"}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="supplier-claim-form">
            <Field
              label={lang === "es" ? "Correo de empresa" : "Business email"}
              required
            >
              <input
                type="email"
                className="cd-input"
                required
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                data-testid="claim-email"
              />
            </Field>
            <Field
              label={lang === "es" ? "Nombre completo" : "Full name"}
              required
            >
              <input
                type="text"
                className="cd-input"
                required
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                data-testid="claim-name"
              />
            </Field>
            <Field label={lang === "es" ? "Puesto" : "Title"}>
              <input
                type="text"
                className="cd-input"
                value={form.title}
                onChange={(e) => update("title", e.target.value)}
                data-testid="claim-title"
              />
            </Field>
            <Field
              label={lang === "es" ? "Confirma tu número IMMEX" : "Confirm your IMMEX number"}
              required
              hint={
                lang === "es"
                  ? "Debe coincidir con nuestros registros para confirmar la propiedad."
                  : "Must match our records to confirm ownership."
              }
            >
              <input
                type="text"
                className="cd-input"
                required
                value={form.immex_number}
                onChange={(e) => update("immex_number", e.target.value)}
                data-testid="claim-immex"
              />
            </Field>
            {error ? (
              <div
                className="flex items-start gap-2 text-sm"
                style={{ color: "var(--red)" }}
                data-testid="claim-error"
              >
                <AlertCircle size={14} style={{ marginTop: 2 }} />
                <span>{error}</span>
              </div>
            ) : null}
            <button
              type="submit"
              disabled={submitting}
              className="btn btn-primary w-full inline-flex items-center justify-center gap-2"
              data-testid="claim-submit"
            >
              {submitting
                ? lang === "es" ? "Enviando…" : "Submitting…"
                : (
                  <>
                    <Mail size={14} />
                    {lang === "es" ? "Enviar reclamación" : "Submit claim"}
                  </>
                )}
            </button>
            <p className="text-xs" style={{ color: "var(--ink-2)" }}>
              {lang === "es"
                ? "Sin tarjeta. Sin spam. Cancelas cuando quieras."
                : "No card. No spam. Cancel anytime."}
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div>
      <label className="overline">
        {label}
        {required ? <span style={{ color: "var(--orange)" }}> *</span> : null}
      </label>
      <div className="mt-2">{children}</div>
      {hint ? (
        <p className="mt-1 text-xs" style={{ color: "var(--ink-2)" }}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Buyer-activity urgency card — only rendered for unclaimed profiles.
 * Spanish-only by spec since it targets Mexican plant managers (the
 * audience for claims). Two states:
 *   • views > 0 → social-proof variant with blurred placeholder names
 *   • views === 0 → "be the first" variant
 * Both end with the same claim CTA which opens the existing ClaimModal.
 */
function BuyerActivityBlock({ views, onClaimClick }) {
  const hasViews = (views || 0) > 0;
  return (
    <div
      data-testid="buyer-activity-block"
      style={{
        marginTop: 28,
        background: "#0a0a0a",
        color: "#fff",
        border: "1px solid #2a2a2a",
        padding: "20px 22px",
        maxWidth: 640,
      }}
    >
      <div
        data-testid="buyer-activity-heading"
        style={{
          fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
          fontSize: 11,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "#FF6B00",
          fontWeight: 600,
        }}
      >
        📊 Actividad reciente en tu perfil
      </div>

      {hasViews ? (
        <>
          <p
            data-testid="buyer-activity-headline"
            style={{
              marginTop: 12,
              fontSize: 14,
              lineHeight: 1.55,
              color: "#e5e5e5",
            }}
          >
            Esta semana,{" "}
            <strong style={{ color: "#fff" }} data-testid="buyer-activity-count">
              {views}
            </strong>{" "}
            {views === 1
              ? "comprador de EE.UU. visitó perfiles con capacidades similares a las tuyas."
              : "compradores de EE.UU. visitaron perfiles con capacidades similares a las tuyas."}
          </p>
          <div
            aria-hidden="true"
            data-testid="buyer-activity-blurred-lines"
            style={{
              marginTop: 14,
              display: "flex",
              flexDirection: "column",
              gap: 6,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12.5,
              color: "#bdbdbd",
              filter: "blur(4px)",
              userSelect: "none",
            }}
          >
            <div>██████ ██████████ — Director de Compras</div>
            <div>██████ ████ — Supply Chain Manager</div>
          </div>
          <p
            style={{
              marginTop: 14,
              fontSize: 13.5,
              lineHeight: 1.5,
              color: "#e5e5e5",
            }}
          >
            Reclama tu perfil para recibir sus RFQs directo a tu correo.
          </p>
        </>
      ) : (
        <p
          data-testid="buyer-activity-headline-zero"
          style={{
            marginTop: 12,
            fontSize: 14,
            lineHeight: 1.55,
            color: "#e5e5e5",
          }}
        >
          Sé el primero en ser descubierto por compradores de EE.UU. — reclama tu perfil gratis.
        </p>
      )}

      <button
        type="button"
        onClick={onClaimClick}
        data-testid="buyer-activity-claim-cta"
        style={{
          marginTop: 16,
          background: "#FF6B00",
          color: "#fff",
          border: 0,
          padding: "12px 18px",
          fontFamily: "'Bebas Neue', 'Archivo Black', system-ui, sans-serif",
          fontSize: 14,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        Reclamar perfil gratis →
      </button>
    </div>
  );
}
